// Runtime type guards for untrusted JSON responses. Per RFC-009 we treat every
// response body as `unknown` and narrow it here rather than asserting with `as`.

import type { ChallengeInfo, TokenInfo } from "./types";

/** True for a non-null object we can index by string key. */
export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

const isNum = (v: unknown): v is number => typeof v === "number";
const isStr = (v: unknown): v is string => typeof v === "string";

/** Safely read a string field from an unknown value. */
export function strField(v: unknown, key: string): string | undefined {
  if (!isRecord(v)) return undefined;
  const val = v[key];
  return typeof val === "string" ? val : undefined;
}

/** True when `v` has the shape the token-inspect renderer relies on. */
export function isTokenInfo(v: unknown): v is TokenInfo {
  return (
    isRecord(v) &&
    isNum(v["token_type"]) &&
    isStr(v["nonce"]) &&
    isStr(v["challenge_digest"]) &&
    isStr(v["token_key_id"]) &&
    isNum(v["authenticator_len"]) &&
    Array.isArray(v["extensions"])
  );
}

/** True when `v` has the shape the challenge-inspect renderer relies on. */
export function isChallengeInfo(v: unknown): v is ChallengeInfo {
  return (
    isRecord(v) &&
    isNum(v["token_type"]) &&
    isStr(v["issuer_name"]) &&
    Array.isArray(v["origin_info"]) &&
    isStr(v["challenge_digest"])
  );
}
